import React, { useState, useEffect } from 'react';
import { searchByUsername } from '../../../services/api/userService';
import teamService from '../../../services/api/teamService';

/**
 * InviteUserModal
 * Simplified — username search + Starter/Substitute role only (no message, no admin role).
 */
const InviteUserModal = ({ team, currentUser, onClose, onInviteSuccess }) => {
  const [searchTerm, setSearchTerm]       = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]         = useState(false);
  const [selectedUser, setSelectedUser]   = useState(null);
  const [memberType, setMemberType]       = useState('primary'); // 'primary' | 'substitute'
  const [sending, setSending]             = useState(false);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  // Derive roster capacity from team settings
  const maxPrimary   = team?.max_members       ?? team?.settings?.max_members       ?? null;
  const maxSubstitute = team?.max_substitutes  ?? team?.settings?.max_substitutes   ?? null;

  // Debounced username search
  useEffect(() => {
    if (searchTerm.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        setError('');
        const res = await searchByUsername(searchTerm);
        const users = res.data?.users || [];
        setSearchResults(users.filter(u => u.id !== currentUser?.id));
      } catch (err) {
        setError('Failed to search users');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, currentUser?.id]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm('');
    setSearchResults([]);
    setError('');
    setSuccess('');
  };

  const handleSendInvitation = async () => {
    if (!selectedUser) { setError('Please select a player to invite'); return; }
    try {
      setSending(true);
      setError('');
      setSuccess('');
      // Pass member_type so the backend can place them in the right roster slot
      await teamService.inviteUserToTeam(team.id, {
        userId: selectedUser.id,
        requestedRole: 'player',
        member_type: memberType,
        message: `You have been invited to join ${team.name}!`,
      });
      setSuccess(`Invitation sent to @${selectedUser.username}!`);
      setTimeout(() => { onInviteSuccess?.(); onClose(); }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="confirm-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="confirm-modal invite-user-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="confirm-modal-header invite-modal-header">
          <h3 className="confirm-modal-title">Invite Player to {team.name}</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="invite-modal-body">
          {error   && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* ── Search ─────────────────────────────────────────────────── */}
          {!selectedUser && (
            <div className="form-group">
              <label htmlFor="searchUser" style={{ color: "rgba(255,255,255,0.8)", marginBottom: "0.4rem", display: "block" }}>
                Search by Username
              </label>
              <input
                type="text"
                id="searchUser"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Type username to search…"
                disabled={sending}
                style={{ color: "#fff", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", padding: "0.6rem 0.75rem", width: "100%", outline: "none", fontSize: "0.9rem" }}
              />
              {searching && <div className="searching-indicator" style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", marginTop: "0.3rem" }}>Searching…</div>}

              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(user => (
                    <div key={user.id} className="search-result-item" onClick={() => handleSelectUser(user)}
                      style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.55rem 0.75rem", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.06)", transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(0,255,136,0.06)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <span style={{ fontSize: "0.85rem", color: "#00ff88", fontWeight: 600 }}>@{user.username}</span>
                      <span style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.6)" }}>{user.first_name} {user.last_name}</span>
                    </div>
                  ))}
                </div>
              )}

              {searchTerm.length >= 2 && !searching && searchResults.length === 0 && (
                <div style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.45)", marginTop: "0.3rem" }}>No players found</div>
              )}
            </div>
          )}

          {/* ── Selected user ───────────────────────────────────────────── */}
          {selectedUser && (
            <div className="selected-user" style={{ display: "flex", alignItems: "center", gap: "0.75rem", background: "rgba(0,255,136,0.06)", border: "1px solid rgba(0,255,136,0.2)", borderRadius: "8px", padding: "0.65rem 0.9rem", marginBottom: "0.9rem" }}>
              <div style={{ flex: 1 }}>
                <span style={{ color: "#00ff88", fontWeight: 600, fontSize: "0.9rem" }}>@{selectedUser.username}</span>
                <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", marginLeft: "0.5rem" }}>{selectedUser.first_name} {selectedUser.last_name}</span>
              </div>
              <button onClick={() => setSelectedUser(null)} disabled={sending}
                style={{ background: "none", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "6px", color: "rgba(255,255,255,0.7)", padding: "0.25rem 0.6rem", cursor: "pointer", fontSize: "0.8rem" }}
              >Change</button>
            </div>
          )}

          {/* ── Roster slot ─────────────────────────────────────────────── */}
          {selectedUser && (
            <div className="form-group" style={{ marginBottom: "0.9rem" }}>
              <label style={{ color: "rgba(255,255,255,0.8)", marginBottom: "0.4rem", display: "block", fontSize: "0.88rem" }}>
                Roster Slot
              </label>
              <div style={{ display: "flex", gap: "0.6rem" }}>
                {[
                  { value: "primary",    label: `🏃 Starter ${maxPrimary ? `(up to ${maxPrimary})` : ''}` },
                  { value: "substitute", label: `🔄 Substitute ${maxSubstitute ? `(up to ${maxSubstitute})` : ''}` },
                ].map(opt => (
                  <button key={opt.value} onClick={() => setMemberType(opt.value)} disabled={sending}
                    style={{
                      flex: 1, padding: "0.55rem 0.5rem", borderRadius: "8px", fontSize: "0.85rem", cursor: "pointer", transition: "all 0.15s",
                      background: memberType === opt.value ? "rgba(0,255,136,0.15)" : "rgba(255,255,255,0.05)",
                      border: memberType === opt.value ? "1px solid rgba(0,255,136,0.4)" : "1px solid rgba(255,255,255,0.15)",
                      color: memberType === opt.value ? "#00ff88" : "rgba(255,255,255,0.7)",
                      fontWeight: memberType === opt.value ? 600 : 400,
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="confirm-modal-actions invite-modal-actions">
          <button className="confirm-btn cancel" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="confirm-btn" onClick={handleSendInvitation}
            disabled={!selectedUser || sending}
            title={!selectedUser ? 'Select a player first' : 'Send invitation'}
          >
            {sending ? 'Sending…' : 'Send Invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;

